import { useState, useContext, useEffect, useRef, Dispatch, SetStateAction } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, Typography, FormControlLabel, FormControl, Button, RadioGroup, Radio, Grid, Paper, Fade } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { APIProvider, useMapsLibrary, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { subHours } from 'date-fns';

import { IProject } from '../../../types/project';
import { IUser } from '../../../types/user';
import { convertAllDates, isDST } from '../../../utils/Functions';
import { validateEmail } from '../../../utils/Validations';
import { addSeconds } from 'date-fns';
import { AppointmentContext } from '../../../context/AppointmentContext';
import PurchaserForm from './PurchaserForm';
import { FlexEnd } from '../../../utils/CommonStyles';

const Main = (props: ChildProps) => {
  const { schedule, timeSlots, project, user, submitAppointment, registrant, setRegistrant } = props;
  const [appointment, dispatchAppointment] = useContext(AppointmentContext);
  const [date, setDate] = useState<Date | null>(null);
  const [position, setPosition] = useState<IPosition | null>({
    lat: 0,
    lng: 0,
  });
  const [open, setOpen] = useState<boolean>(false);

  const isTimeslotPast = (day: Date, slot: any) => {
    const now = new Date();
    const isToday =
      day.getFullYear() === now.getFullYear() && day.getMonth() === now.getMonth() && day.getDate() === now.getDate();
    if (!isToday) return false;
    const endTotalMinutes = slot.startHour * 60 + slot.startMin + slot.length;
    const slotEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, endTotalMinutes);
    return slotEnd <= now;
  };

  const dayHasAvailableSlot = (day: Date) => {
    const daySlots =
      timeSlots[day.getFullYear()] && timeSlots[day.getFullYear()][day.getMonth()] && timeSlots[day.getFullYear()][day.getMonth()][day.getDate()];
    if (!daySlots) return false;
    return daySlots.some((slot: any) => slot.numAvailable > 0 && !isTimeslotPast(day, slot));
  };

  useEffect(() => {
    if (
      schedule?.schedules?.length === 1 &&
      schedule?.schedules[0].date &&
      (schedule?.schedules[0].timeEnd - schedule?.schedules[0].timeStart) / schedule?.schedules[0].length === 60
    ) {
      dispatchAppointment({
        type: 'SET_DATE',
        payload: {
          value: new Date(addSeconds(new Date(schedule?.schedules[0].date).setHours(0, 0, 0, 0), schedule?.schedules[0].timeStart)),
        },
      });
    } else if (schedule?.schedules?.length === 1 && schedule?.schedules[0].date) {
      setDate(new Date(new Date(schedule?.schedules[0].date).setHours(0, 0, 0, 0)));
    } else {
      if (schedule?.schedules?.length > 1 && schedule?.schedules[0].date) {
        let sortedByDate = [...schedule.schedules].sort(
          (a: any, b: any) => new Date(a.date).valueOf() - new Date(b.date).valueOf()
        );

        // Prefer the earliest upcoming day that still has an available, non-past timeslot -
        // e.g. if today's last appointment time has already passed, skip ahead to the next day.
        let nextAvailable = sortedByDate.find((setting: any) => {
          const day = new Date(new Date(setting.date).setHours(0, 0, 0, 0));
          return day.valueOf() >= new Date(new Date().setHours(0, 0, 0, 0)).valueOf() && dayHasAvailableSlot(day);
        });

        if (nextAvailable) {
          setDate(new Date(new Date(nextAvailable.date).setHours(0, 0, 0, 0)));
        } else {
          let closestDate = [...schedule.schedules].sort((a: any, b: any) => {
            let distanceA = Math.abs(new Date().valueOf() - new Date(a.date).valueOf());
            let distanceB = Math.abs(new Date().valueOf() - new Date(b.date).valueOf());
            return distanceA - distanceB;
          });

          setDate(new Date(new Date(closestDate[0].date).setHours(0, 0, 0, 0)));
        }
      }
    }
    if (schedule?.locations.length === 1) {
      dispatchAppointment({ type: 'SET_LOCATION', payload: { value: schedule.locations[0] } });
    }
    // eslint-disable-next-line
  }, []);

  function disableDates(date: Date) {
    let allDates = schedule.schedules.map((schedule: any) => new Date(new Date(schedule.date).setHours(0, 0, 0, 0)).valueOf());
    return !allDates.includes(date.valueOf());
  }

  const appointmentsExist = (date: Date) => {
    return (
      timeSlots[date.getFullYear()] &&
      timeSlots[date.getFullYear()][date.getMonth()] &&
      timeSlots[date.getFullYear()][date.getMonth()][date.getDate()]
    );
  };

  const reservationInfoRef = useRef<HTMLDivElement>(null);
  const scrollToReservationInfo = useRef(false);

  const selectTime = (value: string | null) => {
    const dateValue = value ? new Date(value) : null;
    scrollToReservationInfo.current = !!dateValue;
    dispatchAppointment({ type: 'SET_DATE', payload: { value: dateValue } });
  };

  useEffect(() => {
    if (appointment.date && scrollToReservationInfo.current) {
      scrollToReservationInfo.current = false;
      reservationInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [appointment.date]);

  function Geocoding() {
    const geocodingLibrary = useMapsLibrary('geocoding');
    if (!geocodingLibrary) return null;
    const geocoder = new geocodingLibrary.Geocoder();

    // ...
    geocoder.geocode({ address: appointment.location }, function (results, status) {
      if (status === google.maps.GeocoderStatus.OK) {
        if (results?.length) {
          if (position && position.lat !== results[0].geometry.location.lat() && position.lng !== results[0].geometry.location.lng()) {
            setPosition({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
          }
          return null;
        } else return null;
      } else return null;
    });
    return null;
  }

  const showMap = !!(appointment.location && !appointment.location.includes('http'));

  const logosSection = (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        rowGap: 2,
      }}
    >
      {project.developerLogoGetUrl ? (
        <Box
          sx={{
            height: '100px',
            alignContent: 'center',
            mr: 3,
            mt: 2,
            '@media (max-width: 920px)': {
              height: '70px',
            },
            '@media (max-width: 600px)': {
              mr: 1.5,
              height: '60px',
            },
          }}
        >
          <a style={{ textDecoration: 'none', color: '#000' }} target="_blank" rel="noreferrer" href={`https://${project.portal.officialSite}`}>
            <img src={project.developerLogoGetUrl!} style={{ maxWidth: '100%', objectFit: 'cover', height: '100%' }} alt="Logo" />
          </a>
        </Box>
      ) : null}
      {project.logoGetUrl ? (
        <Box
          sx={{
            height: '100px',
            alignContent: 'center',
            mr: 3,
            mt: 2,
            '@media (max-width: 920px)': {
              height: '70px',
            },
            '@media (max-width: 600px)': {
              mr: 1.5,
              height: '60px',
            },
          }}
        >
          <a style={{ textDecoration: 'none', color: '#000' }} target="_blank" rel="noreferrer" href={`https://${project.portal.officialSite}`}>
            <img src={project.logoGetUrl!} style={{ maxWidth: '100%', objectFit: 'cover', height: '100%' }} alt="Logo" />
          </a>
        </Box>
      ) : null}
      <Box
        sx={{
          height: '100px',
          alignContent: 'center',
          mt: 2,
          '@media (max-width: 920px)': {
            height: '70px',
          },
          '@media (max-width: 600px)': {
            height: '60px',
          },
        }}
      >
        <a style={{ textDecoration: 'none', color: '#000' }} target="_blank" rel="noreferrer" href={`https://rdsbrokerage.com`}>
          <img
            src={'https://s3.ca-central-1.amazonaws.com/app.rdsre.ca/logo.png'}
            style={{ maxWidth: '100%', objectFit: 'cover', height: '100%' }}
            alt="Logo"
          />
        </a>
      </Box>
    </Box>
  );

  const hasFixedDate =
    schedule?.schedules?.length === 1 &&
    schedule?.schedules[0].date &&
    (schedule?.schedules[0].timeEnd - schedule?.schedules[0].timeStart) / schedule?.schedules[0].length === 60;

  const locationSection =
    !/[0-9]/.test(schedule.locations[0]) || schedule.hideLocation ? null : (
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        {schedule.locations.length > 1 ? (
          <FormControl fullWidth>
            <Typography variant="h3" sx={{ color: '#002044', fontWeight: 600, mb: 2 }}>
              Select Location
            </Typography>
            <RadioGroup
              aria-labelledby="radio-label"
              name="address-radio"
              value={appointment.location}
              onChange={(e) => dispatchAppointment({ type: 'SET_LOCATION', payload: { value: e.target.value } })}
            >
              <Grid container spacing={2}>
                {schedule.locations.map((location: string, index: number) => {
                  return (
                    <Grid item xs={12} sm={4} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderColor: appointment.location === location ? '#002044' : 'rgba(0, 0, 0, 0.15)',
                          backgroundColor: appointment.location === location ? 'rgba(0, 32, 68, 0.05)' : '#fff',
                        }}
                      >
                        <FormControlLabel value={location} control={<Radio />} label={location} sx={{ mr: 0.5, flex: 1 }} />
                        <a
                          style={{ textDecoration: 'none', color: '#000' }}
                          target="_blank"
                          rel="noreferrer"
                          href={`https://www.google.com/maps?q=${schedule.locations[0]}`}
                        >
                          <LocationOnIcon sx={{ color: 'red', fontSize: '18px', pt: 0.5 }} />
                        </a>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </FormControl>
        ) : (
          <Box>
            <Typography variant="h3" sx={{ color: '#002044', fontWeight: 600, mb: 1 }}>
              Location
            </Typography>
            <Box>
              <a
                style={{ textDecoration: 'none', color: '#000' }}
                target="_blank"
                rel="noreferrer"
                href={`https://www.google.com/maps?q=${schedule.locations[0]}`}
              >
                <strong>
                  {schedule.locations[0]} <LocationOnIcon sx={{ color: 'red', fontSize: '16px' }} />
                </strong>
              </a>
            </Box>
          </Box>
        )}
      </Paper>
    );

  return (
    <Box sx={{ backgroundColor: '#f4f6f9', minHeight: '100vh', flexShrink: 0, pb: 6 }}>
      <Box sx={{ backgroundColor: '#002044', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)' }}>
        <Typography variant="h2" color="#fff" sx={{ textAlign: 'center', p: 3 }}>
          {schedule.name}
        </Typography>
      </Box>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 } }}>
        <form onSubmit={submitAppointment}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              {schedule.mainText ? (
                <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                  <Box style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: `${schedule.mainText}` }}></Box>
                </Paper>
              ) : null}
              {locationSection}
              <Box sx={{ mb: 3 }}>{logosSection}</Box>
            </Grid>
            {showMap ? (
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: 500,
                      width: '100%',
                      '@media (max-width: 600px)': {
                        height: 300,
                      },
                    }}
                  >
                    <APIProvider apiKey={process.env.REACT_APP_REACT_MAPS!}>
                      <Geocoding />
                      {position && position.lat && position.lng ? (
                        <Map defaultCenter={position} defaultZoom={15} mapId="DEMO_MAP_ID">
                          <AdvancedMarker position={position} />
                        </Map>
                      ) : null}
                    </APIProvider>
                  </Box>
                </Paper>
              </Grid>
            ) : null}
          </Grid>
          {hasFixedDate ? (
            <Paper variant="outlined" sx={{ p: 3, mt: 4, mb: 3, borderRadius: 2 }}>
              <Typography variant="h3" sx={{ color: '#002044', fontWeight: 600, mb: 1 }}>
                Date
              </Typography>
              <Box>{appointment.date ? convertAllDates(new Date(appointment.date), 'PPPPPp') : null}</Box>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, mt: 4, mb: 3, borderRadius: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3" sx={{ color: '#002044', fontWeight: 600 }}>
                  Select Date
                </Typography>
                <Grid container spacing={2} sx={{ mt: 0.2 }}>
                  <Grid item xs={12} sm={4} sx={{ cursor: 'pointer' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date"
                        value={date}
                        open={open}
                        onChange={(newValue) => {
                          dispatchAppointment({ type: 'SET_DATE', payload: { value: null } });
                          setOpen(false);
                          setDate(newValue);
                        }}
                        slotProps={{
                          textField: {
                            onClick: () => setOpen(!open),
                            inputProps: {
                              readOnly: true,
                            },
                          },
                        }}
                        slots={{
                          openPickerButton: () => null,
                        }}
                        shouldDisableDate={disableDates}
                        disablePast={true}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>
              {!appointment.date && date ? (
                <Typography sx={{ mb: 1 }}>
                  <strong>Please select a timeslot below</strong>
                </Typography>
              ) : null}
              {date ? (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <strong>{convertAllDates(date, 'PPPP')}</strong>
                  </Box>
                  <Fade in={true} timeout={2000}>
                    <RadioGroup>
                      <Grid container spacing={2}>
                        {date &&
                          appointmentsExist(date) &&
                          timeSlots[date.getFullYear()][date.getMonth()][date.getDate()]
                            .sort((a: any, b: any) => a.startHour - b.startHour || a.startMin - b.startMin)
                            .map((value: any, index: number) => {
                              const selected =
                                !!appointment.date &&
                                appointment.date.getHours() === value.startHour &&
                                appointment.date.getMinutes() === value.startMin;
                              const isPast = isTimeslotPast(date, value);
                              const isDisabled = value.numAvailable < 1 || isPast;
                              return (
                                <Grid item xs={12} sm={6} md={3} xl={2}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      height: '100%',
                                      borderRadius: 2,
                                      textAlign: 'center',
                                      cursor: 'pointer',
                                      transition: 'box-shadow 0.2s ease',
                                      borderColor: selected ? '#23c323' : 'rgba(0, 0, 0, 0.15)',
                                      backgroundColor: selected ? '#23c323' : '#fff',
                                      color: selected ? '#fff' : '#000',
                                      opacity: !isDisabled ? 1 : 0.3,
                                      '&:hover': !isDisabled ? { boxShadow: 3 } : undefined,
                                    }}
                                    onClick={() => {
                                      if (!isDisabled) {
                                        selectTime(
                                          new Date(
                                            date.getFullYear(),
                                            date.getMonth(),
                                            date.getDate(),
                                            value.startHour,
                                            value.startMin
                                          ).toISOString()
                                        );
                                      }
                                    }}
                                  >
                                    <FormControlLabel
                                      value={`${convertAllDates(value.dayslot, 'PPPP')} - ${value.timeslot}`}
                                      control={<Radio checked={selected} />}
                                      disabled={isDisabled}
                                      label={value.timeslot}
                                    />
                                    {isDisabled ? (
                                      <Box>
                                        <em>No Slots Available</em>
                                      </Box>
                                    ) : null}
                                  </Paper>
                                </Grid>
                              );
                            })}
                      </Grid>
                    </RadioGroup>
                  </Fade>
                </Box>
              ) : null}
            </Paper>
          )}
          {appointment.date ? (
            <Fade in={true} timeout={2000}>
              <Paper ref={reservationInfoRef} variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <PurchaserForm schedule={schedule} project={project} user={user} registrant={registrant} setRegistrant={setRegistrant} />
              </Paper>
            </Fade>
          ) : null}
          <FlexEnd sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color={appointment.date && appointment.location && validateEmail(appointment.purchaserInfo.email) ? 'success' : 'inherit'}
              disabled={!(appointment.date && appointment.location && validateEmail(appointment.purchaserInfo.email))}
              type="submit"
              sx={{ px: 4, py: 1.2, borderRadius: 2, fontWeight: 600 }}
            >
              Submit
            </Button>
          </FlexEnd>
        </form>
      </Box>
    </Box>
  );
};

interface IPosition {
  lat: number;
  lng: number;
}

interface ChildProps {
  project: IProject;
  user: IUser;
  schedule: any;
  scheduleId?: string;
  timeSlots: any;
  submitAppointment: any;
  registrant: boolean | null;
  setRegistrant: Dispatch<SetStateAction<boolean | null>>;
}

export default Main;
